import { Injectable, OnDestroy } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { RTMonitoringType, RealTimeTelemetryService, VirtualRTStateType, normalizeVirtualRTStatus, getRemainingMs } from 'ngx-core-services';
import { EMPTY, Subscription, from } from 'rxjs';
import { catchError, mergeMap, tap } from 'rxjs/operators';

import {
  activateRealTimeTelemetry,
  deactivateRealTimeTelemetry,
  realTimeTelemetryActivated,
  realTimeTelemetryActivationFailed,
  realTimeTelemetryDataReceived,
  realTimeTelemetryDeactivationFailed,
  realTimeTelemetrySessionEnded,
  resumeRealTimeTelemetry,
} from '../actions/real-time.actions';
import { removeSystem, setSystemUnsubscribed, updateVirtualRTStatus } from '../actions/system.actions';

const RT_DEBUG = false;
const TAG = '[RT-DEBUG][Effects]';
const rtLog = (...args: any[]) => { if (RT_DEBUG) { console.log(...args); } };
const rtWarn = (...args: any[]) => { if (RT_DEBUG) { console.warn(...args); } };

interface RTRuntimeSession {
  plantId: string;
  systemId: string;
  emsId: string;
  monitoringType: RTMonitoringType;
  subscription?: Subscription;
}

@Injectable()
export class RealTimeTelemetryEffects implements OnDestroy {
  private readonly runtimeBySystemId = new Map<string, RTRuntimeSession>();
  private readonly virtualRTActiveBySystemId = new Map<string, boolean>();

  activateRT$ = createEffect(() =>
    this.actions$.pipe(
      ofType(activateRealTimeTelemetry),
      tap((action) => rtLog(`${TAG} activateRT$ action: plant=${action.plantId}, system=${action.systemId}, ems=${action.emsId}, mode=${action.monitoringType}, duration=${action.duration}`)),
      mergeMap((action) =>
        from(this.rtService.startCommand(action.plantId, action.emsId, action.systemId, action.monitoringType, action.duration)).pipe(
          mergeMap((status) => {
            const normalizedStatus = normalizeVirtualRTStatus(status, action.systemId);
            const started = this.startRuntimeSession(
              action.plantId,
              action.systemId,
              action.emsId,
              action.monitoringType,
              normalizedStatus,
              'activate',
            );

            if (!started) {
              return EMPTY;
            }

            return [
              realTimeTelemetryActivated({
                systemId: action.systemId,
                startTS: normalizedStatus.startTS,
                duration: normalizedStatus.duration,
              }),
              updateVirtualRTStatus({
                systemId: action.systemId,
                virtualRTStatus: normalizedStatus,
              }),
            ];
          }),
          catchError((err) => {
            console.error(`${TAG} activateRT$ ERROR`, err);
            return [realTimeTelemetryActivationFailed({ systemId: action.systemId, error: err?.message ?? String(err) })];
          }),
        ),
      ),
    ),
  );

  resumeRT$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resumeRealTimeTelemetry),
      tap((action) => rtLog(`${TAG} resumeRT$ action: plant=${action.plantId}, system=${action.systemId}, ems=${action.emsId}, mode=${action.monitoringType}`)),
      mergeMap((action) => {
        try {
          const normalizedStatus = normalizeVirtualRTStatus(action.virtualRTStatus, action.systemId);
          const started = this.startRuntimeSession(
            action.plantId,
            action.systemId,
            action.emsId,
            action.monitoringType,
            normalizedStatus,
            'resume',
          );

          if (!started) {
            return EMPTY;
          }

          return [
            realTimeTelemetryActivated({
              systemId: action.systemId,
              startTS: normalizedStatus.startTS,
              duration: normalizedStatus.duration,
            }),
            updateVirtualRTStatus({
              systemId: action.systemId,
              virtualRTStatus: normalizedStatus,
            }),
          ];
        } catch (err: any) {
          console.error(`${TAG} resumeRT$ ERROR`, err);
          return [realTimeTelemetryActivationFailed({ systemId: action.systemId, error: err?.message ?? String(err) })];
        }
      }),
    ),
  );

  deactivateRT$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deactivateRealTimeTelemetry),
      tap((action) => rtLog(`${TAG} deactivateRT$ action: plant=${action.plantId}, system=${action.systemId}, ems=${action.emsId}, mode=${action.monitoringType}`)),
      mergeMap((action) =>
        from(this.rtService.stopCommand(action.plantId, action.emsId, action.systemId, action.monitoringType)).pipe(
          mergeMap(() => {
            this.endRuntimeSession(action.systemId, 'manual-deactivation');
            return EMPTY;
          }),
          catchError((err) => {
            console.error(`${TAG} deactivateRT$ ERROR`, err);
            return [realTimeTelemetryDeactivationFailed({ systemId: action.systemId, error: err?.message ?? String(err) })];
          }),
        ),
      ),
    ),
  );

  syncRTSessionWithVirtualStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateVirtualRTStatus),
      mergeMap(({ systemId, virtualRTStatus }) => {
        const normalizedStatus = normalizeVirtualRTStatus(virtualRTStatus, systemId);
        const previousActive = this.virtualRTActiveBySystemId.get(systemId);
        this.virtualRTActiveBySystemId.set(systemId, normalizedStatus.active);

        if (normalizedStatus.active) {
          return EMPTY;
        }

        this.clearRuntimeSession(systemId);
        if (previousActive !== true) {
          return EMPTY;
        }

        rtLog(`${TAG} virtualRTStatus transitioned to inactive system=${systemId}`);
        return [realTimeTelemetrySessionEnded({
          systemId,
          endedAt: Date.now(),
        })];
      }),
    ),
  );

  clearRuntimeOnSystemUnsubscribe$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSystemUnsubscribed, removeSystem),
      tap((action) => {
        const { systemId } = action;
        rtLog(`${TAG} clearRuntimeOnSystemUnsubscribe$ system=${systemId}`);
        if (action.type === removeSystem.type) {
          this.virtualRTActiveBySystemId.delete(systemId);
        }
        this.clearRuntimeSession(systemId);
      }),
    ),
    { dispatch: false },
  );

  constructor(
    private readonly actions$: Actions,
    private readonly rtService: RealTimeTelemetryService,
    private readonly store: Store,
  ) {}

  ngOnDestroy(): void {
    for (const systemId of this.runtimeBySystemId.keys()) {
      this.clearRuntimeSession(systemId);
    }
    this.virtualRTActiveBySystemId.clear();
  }

  private startRuntimeSession(
    plantId: string,
    systemId: string,
    emsId: string,
    monitoringType: RTMonitoringType,
    virtualRTStatus: VirtualRTStateType,
    source: 'activate' | 'resume',
  ): boolean {
    const remainingMs = getRemainingMs(virtualRTStatus);

    this.clearRuntimeSession(systemId);

    if (!virtualRTStatus.active) {
      rtWarn(`${TAG} startRuntimeSession skipped (${source}) system=${systemId}: active=${virtualRTStatus.active}, remainingMs=${remainingMs}`);
      return false;
    }

    const runtime: RTRuntimeSession = {
      plantId,
      systemId,
      emsId,
      monitoringType,
    };

    runtime.subscription = this.rtService.createTelemetryStream(emsId, systemId).subscribe({
      next: ({ systemId: streamSystemId, data }) => {
        if (!data) {
          return;
        }

        this.store.dispatch(realTimeTelemetryDataReceived({
          systemId: streamSystemId,
          data,
        }));
      },
      error: (err) => {
        console.error(`${TAG} telemetry stream ERROR system=${systemId}`, err);
        this.endRuntimeSession(systemId, 'stream-error');
      },
      complete: () => {
        rtLog(`${TAG} telemetry stream COMPLETE system=${systemId}`);
        this.endRuntimeSession(systemId, 'stream-complete');
      },
    });

    this.runtimeBySystemId.set(systemId, runtime);
    rtLog(`${TAG} runtime STARTED (${source}) system=${systemId}, remaining=${Math.round(remainingMs / 1000)}s`);

    return true;
  }

  private clearRuntimeSession(systemId: string): void {
    const runtime = this.runtimeBySystemId.get(systemId);
    if (!runtime) {
      return;
    }

    this.runtimeBySystemId.delete(systemId);
    runtime.subscription?.unsubscribe();

    rtLog(`${TAG} runtime CLEARED system=${systemId}`);
  }

  private endRuntimeSession(systemId: string, reason: string): void {
    const hasRuntime = this.runtimeBySystemId.has(systemId);
    this.clearRuntimeSession(systemId);

    if (!hasRuntime) {
      return;
    }

    rtLog(`${TAG} runtime ENDED system=${systemId}, reason=${reason}`);
  }
}
